import React from 'react'
import { Link } from 'react-router-dom'
import { Box, Drawer, List, ListItem, ListItemButton } from '@mui/material'
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone'
import AutoAwesomeMotionTwoToneIcon from '@mui/icons-material/AutoAwesomeMotionTwoTone'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import StorageTwoToneIcon from '@mui/icons-material/StorageTwoTone'
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'
import CopyAllOutlinedIcon from '@mui/icons-material/CopyAllOutlined'
import AssessmentIcon from '@mui/icons-material/Assessment'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'
import CurrentClientContext from '../../providers/CurrentClientProvider'
import Auth from '../../providers/AuthProvider'
import V3ClientSelect from './V3ClientSelect'
import { Sparkles } from 'lucide-react'
import ClientService from '../../services/ClientService'
import TipContext from '../../providers/GlobalTip'
import {
  buildClientPlanPath,
  buildClientReportsLibraryPath,
  buildV3AddClientPath,
  buildV3AdminClientNotificationsPath,
  buildV3AdminCustomCalendarPath,
  buildV3AdminFlightControlPath,
  buildV3AdminOrbitAiPath,
  buildV3AdminM1AdvisorsPath,
  buildV3AdminContentReviewPath,
  buildV3AdminOrbitMetadataPath,
  buildV3ClientAnalyticsPath,
  buildV3ClientCategoriesPath,
  buildV3ClientClassesPath,
  buildV3ClientDashboardPath,
  buildV3ClientIndieAiPath,
  buildV3ClientImportClassPath,
  buildV3ClientLegacyReportLibraryPath,
  buildV3ClientLocationsPath,
  buildV3ClientMasterClassPath,
  buildV3ClientOtherPlanToolsPath,
  buildV3ClientPlanPreviewPath,
  buildV3ClientPosMappingPath,
  buildV3ClientProfilePath,
  buildV3ClientSetupPath,
  buildV3ClientTagsPath,
  buildV3ClientUploadDataPath,
  buildV3CopyPlanPath,
  buildV3DevToolsPath,
  buildV3HomePath,
  getClientDashboardV3Enabled,
} from '../../services/ClientDashboardV3'

const desktopDrawerWidth = 88
const mobileDrawerWidth = 320

function buildDrawerStyles({ mobileMode = false }) {
  return {
    paper: {
      backgroundColor: '#FFFFFF',
      color: '#344054',
      overflow: mobileMode ? 'auto' : 'visible',
      borderRight: '1px solid #EAECF0',
      boxShadow: '0 18px 40px rgba(16,24,40,0.10)',
    },
    navItem: {
      flexDirection: mobileMode ? 'row' : 'column',
      justifyContent: mobileMode ? 'flex-start' : 'center',
      alignItems: mobileMode ? 'center' : 'center',
      gap: mobileMode ? 1.25 : 0,
      color: '#667085',
      transition: 'all 0.2s ease',
      fontSize: mobileMode ? 14 : 12,
      p: 0,
      px: mobileMode ? 2 : 0,
      borderBottom: '1px solid #F2F4F7',
      py: mobileMode ? 1.5 : 1.25,
      ':hover': {
        color: '#7F56D9',
        backgroundColor: '#F9F5FF',
      },
    },
    navItemSection: {
      position: 'relative',
      flexDirection: mobileMode ? 'column' : 'block',
      alignItems: 'stretch',
      width: '100%',
      '.v3-subNav': mobileMode
        ? {
          display: 'block',
          position: 'static',
          width: '100%',
          marginTop: '4px',
          marginBottom: '8px',
          paddingLeft: '12px',
          backgroundColor: 'transparent',
          color: '#475467',
          fontSize: 12,
          border: 'none',
          boxShadow: 'none',
          borderRadius: 0,
          overflow: 'visible',
          '.v3-subItem': {
            minHeight: '32px',
            width: '100%',
            borderBottom: '1px solid #F2F4F7',
            paddingTop: '5px',
            paddingBottom: '5px',
            paddingLeft: '14px',
            borderRadius: '8px',
            ':hover': {
              backgroundColor: '#F9FAFB',
            },
          },
          '.v3-subItem:last-child': {
            borderBottom: 'none',
          },
        }
        : {
          display: 'none',
          position: 'absolute',
          left: desktopDrawerWidth,
          top: 0,
          zIndex: 1200,
          width: '228px',
          backgroundColor: '#FFFFFF',
          color: '#475467',
          fontSize: 12,
          border: '1px solid #EAECF0',
          boxShadow: '0 20px 44px rgba(16,24,40,0.12)',
          borderRadius: '18px',
          overflow: 'hidden',
          '.v3-subItem': {
            borderBottom: '1px solid #F2F4F7',
            paddingTop: '5px',
            paddingBottom: '5px',
            ':hover': {
              backgroundColor: '#F9FAFB',
            },
          },
          '.v3-subItem:last-child': {
            borderBottom: 'none',
          },
        },
      ...(mobileMode
        ? {}
        : {
          ':hover': {
            '.v3-subNav': {
              display: 'block',
            },
          },
        }),
    },
    navItemIcon: {
      fontSize: mobileMode ? '22px' : '26px',
      mb: mobileMode ? 0 : 0.5,
      flexShrink: 0,
    },
  }
}

function DisabledNavItem({ icon: Icon, label, styles }) {
  return (
    <ListItem disableGutters disablePadding>
      <ListItemButton
        disabled
        aria-disabled="true"
        sx={{
          ...styles.navItem,
          color: '#D0D5DD',
          cursor: 'not-allowed',
        }}
      >
        <Icon sx={{ ...styles.navItemIcon, color: '#D0D5DD' }} />
        {label}
      </ListItemButton>
    </ListItem>
  )
}

function PrimaryNavSection({ items = [], onClose }) {
  if (!items.length) return null

  return (
    <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid #EAECF0' }}>
      <Box sx={{ mb: 1.25, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#667085' }}>
        Menu
      </Box>
      <List disablePadding>
        {items.map((item) => (
          <ListItem key={item.key || item.to || item.label} disableGutters disablePadding>
            {item.disabled ? (
              <ListItemButton disabled aria-disabled="true" sx={{ justifyContent: 'flex-start', borderRadius: '12px', px: 1.5, py: 1.25, color: '#D0D5DD' }}>
                {item.label}
              </ListItemButton>
            ) : (
              <ListItemButton component={Link} to={item.to} onClick={onClose} sx={{ justifyContent: 'flex-start', borderRadius: '12px', px: 1.5, py: 1.25, color: '#344054' }}>
                {item.label}
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  )
}

function ReportLibraryLink({ currentClient, authUser, styles, onClose }) {
  const isDashboardV3Enabled = getClientDashboardV3Enabled(currentClient.client, authUser)
  const reportsHref = buildClientReportsLibraryPath({
    clientCode: currentClient.client.ClientCode,
    clientId: currentClient.client.Id,
    isV3: isDashboardV3Enabled,
  })

  return (
    <ListItem disableGutters disablePadding sx={styles.navItemSection}>
      <ListItemButton component={Link} sx={styles.navItem} to={reportsHref} onClick={onClose}>
        <LibraryBooksIcon sx={styles.navItemIcon} />
        Report Library
      </ListItemButton>
    </ListItem>
  )
}

function ClientDashboardLink({ currentClient, styles, onClose }) {
  const dashboardHref = buildV3ClientDashboardPath(currentClient.client.ClientCode)

  return (
    <ListItem disableGutters disablePadding sx={styles.navItemSection}>
      <ListItemButton component={Link} sx={styles.navItem} to={dashboardHref} onClick={onClose}>
        <AssessmentIcon sx={styles.navItemIcon} />
        Dashboard
      </ListItemButton>
    </ListItem>
  )
}

function getRoleTags(user) {
  return Array.isArray(user?.roleTags)
    ? user.roleTags.map((tag) => String(tag))
    : []
}

function NavContent({ onClose, styles, showPlannerNavContent = true }) {
  const currentClient = CurrentClientContext.useClient()
  const auth = Auth.useAuth()
  const tipContext = TipContext.useMessage()
  const selectedClient = currentClient?.client || {}
  const roleTags = getRoleTags(auth?.user)
  const isPlannerRole = roleTags.includes('2')
  const isManagementOneRole = roleTags.includes('d76bf050-3555-4a15-8e0a-3f6c57eb31a7')
  const canUsePlanningTools = Boolean(isPlannerRole || isManagementOneRole || auth?.checkPermission?.('full-planning'))
  const canUseAdminTools = Boolean(isManagementOneRole || auth?.checkPermission?.(['role-create', 'dev-tools']))
  const canAddClient = Boolean(isManagementOneRole || auth?.checkPermission?.('add-client'))
  const canViewClientInfo = Boolean(isPlannerRole || isManagementOneRole || auth?.checkPermission?.('view-client-info'))
  const canSeeAnalytics = Boolean(isManagementOneRole || auth?.checkPermission?.('role-create'))
  const canSeeMasterClassTools = Boolean(isManagementOneRole || auth?.checkPermission?.('role-create'))
  const shouldLockReportsToAdmin = Boolean(process.env.REACT_APP_REPORT_LOCKOUT_CURRENT_MONTH)
  const hasClientContext = Boolean(selectedClient?.ClientCode && (selectedClient?.Id || selectedClient?.id))
  const livePlanHref = buildClientPlanPath({
    clientCode: selectedClient?.ClientCode,
    client: selectedClient,
    preferredMode: 'v3',
  })

  const clearCache = async (action) => {
    if (confirm('This action purge all cache for client. Are you sure?') === true) {
      if (action === 'regenerate') {
        await ClientService.regenerateCache(currentClient.client.Id)
        tipContext.showSuccess('Cache regenerating')
      } else {
        await ClientService.clearCache(currentClient.client.Id)
        tipContext.showSuccess('Cache cleared')
      }
      onClose?.()
    }
  }

  if (!showPlannerNavContent) {
    return null
  }

  return (
    <List disablePadding>
      <ListItem disableGutters disablePadding>
        <ListItemButton component={Link} sx={styles.navItem} to={buildV3HomePath()} onClick={onClose}>
          <HomeTwoToneIcon sx={styles.navItemIcon} />
          Home
        </ListItemButton>
      </ListItem>

      {canUsePlanningTools ? (
        <ListItem disableGutters disablePadding sx={styles.navItemSection}>
          <ListItemButton component={Link} sx={styles.navItem} to={buildV3CopyPlanPath()} onClick={onClose}>
            <CopyAllOutlinedIcon sx={styles.navItemIcon} />
            Copy Plan
          </ListItemButton>
        </ListItem>
      ) : null}

      {canAddClient ? (
        <ListItem disableGutters disablePadding sx={styles.navItemSection}>
          <ListItemButton component={Link} sx={styles.navItem} to={buildV3AddClientPath()} onClick={onClose}>
            <LocalHospitalIcon sx={styles.navItemIcon} />
            Add Client
          </ListItemButton>
        </ListItem>
      ) : null}

      {canUseAdminTools ? (
        <ListItem disableGutters disablePadding sx={styles.navItemSection}>
          <ListItemButton sx={styles.navItem}>
            <AutoAwesomeMotionTwoToneIcon sx={styles.navItemIcon} />
            Admin Tools
          </ListItemButton>
          <Box className="v3-subNav" onClick={(event) => event.stopPropagation()}>
            <ListItemButton component={Link} to={buildV3AdminCustomCalendarPath()} className="v3-subItem" onClick={onClose}>Custom Calendars</ListItemButton>
            <ListItemButton component={Link} to={buildV3AdminClientNotificationsPath()} className="v3-subItem" onClick={onClose}>Client Notifications</ListItemButton>
            <ListItemButton component={Link} to={buildV3AdminM1AdvisorsPath()} className="v3-subItem" onClick={onClose}>M1 Advisors</ListItemButton>
            <ListItemButton component={Link} to={buildV3AdminOrbitAiPath()} className="v3-subItem" onClick={onClose}>AI Admin</ListItemButton>
            <ListItemButton component={Link} to={buildV3AdminOrbitMetadataPath()} className="v3-subItem" onClick={onClose}>Orbit Metadata</ListItemButton>
            <ListItemButton component={Link} to={buildV3AdminContentReviewPath()} className="v3-subItem" onClick={onClose}>Curated Content</ListItemButton>
            {Boolean(isManagementOneRole || auth?.checkPermission?.('dev-tools')) ? (
              <ListItemButton component={Link} to={buildV3DevToolsPath()} className="v3-subItem" onClick={onClose}>Dev Tools</ListItemButton>
            ) : null}
            <ListItemButton component={Link} to={buildV3AdminFlightControlPath()} className="v3-subItem" onClick={onClose}>Flight Control</ListItemButton>
            {hasClientContext ? (
              <ListItemButton
                component={Link}
                to={buildV3ClientLegacyReportLibraryPath(selectedClient.ClientCode, selectedClient.Id || selectedClient.id)}
                className="v3-subItem"
                onClick={onClose}
              >
                Legacy Reports
              </ListItemButton>
            ) : null}
          </Box>
        </ListItem>
      ) : null}

      {hasClientContext ? (
        <>
          <ClientDashboardLink currentClient={currentClient} styles={styles} onClose={onClose} />

          <ListItem disableGutters disablePadding sx={styles.navItemSection}>
            <ListItemButton component={Link} sx={styles.navItem} to={buildV3ClientIndieAiPath(currentClient.client.ClientCode)} state={{ source: 'dashboard-ask-indie' }} onClick={onClose}>
              <AutoAwesomeMotionTwoToneIcon sx={styles.navItemIcon} />
              Indie AI
            </ListItemButton>
          </ListItem>

          {canSeeAnalytics ? (
            <ListItem disableGutters disablePadding sx={styles.navItemSection}>
              <ListItemButton component={Link} sx={styles.navItem} to={buildV3ClientAnalyticsPath(currentClient.client.ClientCode, currentClient.client.Id)} onClick={onClose}>
                <AssessmentIcon sx={styles.navItemIcon} />
                Analytics
              </ListItemButton>
            </ListItem>
          ) : null}

          {shouldLockReportsToAdmin
            ? canSeeAnalytics
              ? <ReportLibraryLink currentClient={currentClient} authUser={auth.user} styles={styles} onClose={onClose} />
              : null
            : <ReportLibraryLink currentClient={currentClient} authUser={auth.user} styles={styles} onClose={onClose} />}

          <ListItem disableGutters disablePadding sx={styles.navItemSection}>
            <ListItemButton component={Link} sx={styles.navItem} to={buildV3ClientUploadDataPath(currentClient.client.ClientCode)} onClick={onClose}>
              <StorageTwoToneIcon sx={styles.navItemIcon} />
              Data
            </ListItemButton>
            <Box className="v3-subNav" onClick={(event) => event.stopPropagation()}>
              <ListItemButton component={Link} to={buildV3ClientUploadDataPath(currentClient.client.ClientCode)} className="v3-subItem" onClick={onClose}>Upload Data</ListItemButton>
            </Box>
          </ListItem>

          {canUsePlanningTools ? (
            <ListItem disableGutters disablePadding sx={styles.navItemSection}>
              <ListItemButton component={Link} sx={styles.navItem} to={livePlanHref} onClick={onClose}>
                <TimelineOutlinedIcon sx={styles.navItemIcon} />
                Plan
              </ListItemButton>
              <Box className="v3-subNav" onClick={(event) => event.stopPropagation()}>
                <ListItemButton component={Link} to={buildV3ClientPlanPreviewPath(currentClient.client.ClientCode, currentClient.client.Id)} className="v3-subItem" onClick={onClose}>Plan Preview</ListItemButton>
                <ListItemButton component="button" type="button" onClick={() => clearCache('regenerate')} className="v3-subItem">Regenerate Cache</ListItemButton>
                <ListItemButton component="button" type="button" onClick={() => clearCache('clear')} className="v3-subItem">Clear Cache</ListItemButton>
                <ListItemButton component={Link} to={buildV3ClientOtherPlanToolsPath(currentClient.client.ClientCode)} className="v3-subItem" onClick={onClose}>Other Plan Tools</ListItemButton>
              </Box>
            </ListItem>
          ) : null}

          <ListItem disableGutters disablePadding sx={styles.navItemSection}>
            <ListItemButton component={Link} sx={styles.navItem} to={buildV3ClientProfilePath(currentClient.client.ClientCode)} onClick={onClose}>
              <BusinessOutlinedIcon sx={styles.navItemIcon} />
              Profile
            </ListItemButton>
            {canViewClientInfo ? (
              <Box className="v3-subNav" onClick={(event) => event.stopPropagation()}>
                <ListItemButton component={Link} to={buildV3ClientLocationsPath(currentClient.client.ClientCode)} className="v3-subItem" onClick={onClose}>Locations</ListItemButton>
                <ListItemButton component={Link} to={buildV3ClientClassesPath(currentClient.client.ClientCode)} className="v3-subItem" onClick={onClose}>Classes</ListItemButton>
                <ListItemButton component={Link} to={buildV3ClientCategoriesPath(currentClient.client.ClientCode)} className="v3-subItem" onClick={onClose}>Categories</ListItemButton>
                <ListItemButton component={Link} to={buildV3ClientImportClassPath(currentClient.client.ClientCode)} className="v3-subItem" onClick={onClose}>Import Class</ListItemButton>
                <ListItemButton component={Link} to={buildV3ClientPosMappingPath(currentClient.client.ClientCode)} className="v3-subItem" onClick={onClose}>POS Mapping</ListItemButton>
                {canSeeMasterClassTools ? (
                  <>
                    <ListItemButton component={Link} to={buildV3ClientMasterClassPath(currentClient.client.ClientCode)} className="v3-subItem" onClick={onClose}>Master Class</ListItemButton>
                    <ListItemButton component={Link} to={buildV3ClientTagsPath(currentClient.client.ClientCode)} className="v3-subItem" onClick={onClose}>Tags</ListItemButton>
                  </>
                ) : null}
                {canAddClient ? (
                  <ListItemButton component={Link} to={buildV3ClientSetupPath(currentClient.client.ClientCode)} className="v3-subItem" onClick={onClose}>Setup</ListItemButton>
                ) : null}
              </Box>
            ) : null}
          </ListItem>
        </>
      ) : (
        <>
          <DisabledNavItem icon={AssessmentIcon} label="Dashboard" styles={styles} />
          <DisabledNavItem icon={AutoAwesomeMotionTwoToneIcon} label="Indie AI" styles={styles} />
          {canSeeAnalytics ? <DisabledNavItem icon={AssessmentIcon} label="Analytics" styles={styles} /> : null}
          {(!shouldLockReportsToAdmin || canSeeAnalytics) ? <DisabledNavItem icon={LibraryBooksIcon} label="Report Library" styles={styles} /> : null}
          <DisabledNavItem icon={StorageTwoToneIcon} label="Data" styles={styles} />
          {canUsePlanningTools ? <DisabledNavItem icon={TimelineOutlinedIcon} label="Plan" styles={styles} /> : null}
          <DisabledNavItem icon={BusinessOutlinedIcon} label="Profile" styles={styles} />
        </>
      )}
    </List>
  )
}

export default function V3LeftDrawer({
  open,
  onClose,
  topOffset = 0,
  persistent = false,
  primaryNavItems = [],
  showPrimaryNavItems = false,
  showPlannerNavContent = true,
  mobileMode = false,
  askIndieHref,
  onOpenAskIndie,
}) {
  const styles = buildDrawerStyles({ mobileMode })
  const drawerPaperWidth = mobileMode ? mobileDrawerWidth : desktopDrawerWidth

  return (
    <Drawer
      anchor="left"
      variant={persistent ? 'permanent' : 'temporary'}
      open={persistent || open}
      onClose={persistent ? undefined : onClose}
      ModalProps={persistent ? undefined : { keepMounted: true }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(16, 24, 40, 0.24)',
        },
        '& .MuiDrawer-paper': {
          ...styles.paper,
          boxShadow: persistent ? 'none' : styles.paper.boxShadow,
          width: drawerPaperWidth,
          top: `${topOffset}px`,
          height: `calc(100% - ${topOffset}px)`,
        },
      }}
    >
      <Box component="nav" sx={{ width: drawerPaperWidth, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        {mobileMode ? (
          <Box sx={{ p: 2, borderBottom: '1px solid #EAECF0' }}>
            <V3ClientSelect className="w-full" placeholder="Select a client" />
          </Box>
        ) : null}
        {showPrimaryNavItems ? <PrimaryNavSection items={primaryNavItems} onClose={onClose} /> : null}
        {showPrimaryNavItems && showPlannerNavContent ? (
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#667085' }}>
            Planner tools
          </Box>
        ) : null}
        <NavContent onClose={onClose} styles={styles} showPlannerNavContent={showPlannerNavContent} />

        {mobileMode ? (
          <Box sx={{ p: 2, borderTop: '1px solid #EAECF0', mt: 'auto' }}>
            {onOpenAskIndie ? (
              <button
                type="button"
                onClick={() => {
                  onClose?.()
                  onOpenAskIndie?.()
                }}
                className="flex w-full h-10 items-center justify-center gap-2 rounded-full bg-[#7F56D9] text-sm font-medium text-white shadow-xs hover:bg-[#6941C6] cursor-pointer"
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.6} />
                Ask Indie
              </button>
            ) : askIndieHref ? (
              <Link
                to={askIndieHref}
                onClick={onClose}
                className="flex w-full h-10 items-center justify-center gap-2 rounded-full bg-[#7F56D9] text-sm font-medium text-white no-underline shadow-xs hover:bg-[#6941C6]"
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.6} />
                Ask Indie
              </Link>
            ) : (
              <button
                type="button"
                className="flex w-full h-10 cursor-not-allowed items-center justify-center gap-2 rounded-full bg-[#7F56D9] text-sm font-medium text-white opacity-70 shadow-xs"
                disabled
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.6} />
                Ask Indie
              </button>
            )}
          </Box>
        ) : null}
      </Box>
    </Drawer>
  )
}

